import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '../Component/Navbar';
import Logger from './Logger';
import '../Css/Update.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import html2canvas from 'html2canvas'; // Import html2canvas for map screenshot
import { saveAs } from 'file-saver'; // Use for downloading files
import { getLoggerBaseURL } from '../utils/getLoggerBaseURL';

const MAX_WAYPOINTS = 25;

const Update = () => {
  const { selectedDriverId, driverEmail } = useParams();
  const [tempLocationLogs, setTempLocationLogs] = useState([]);
  const [groupedLogs, setGroupedLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [error, setError] = useState(null);
  const [maps, setMaps] = useState([]); // Array of maps
  const [infoWindows, setInfoWindows] = useState([]); // Array of info windows
  const [directionsServices, setDirectionsServices] = useState([]); // Array of direction services
  const [directionsRenderers, setDirectionsRenderers] = useState([]); // Array of direction renderers
  const [selectedDate, setSelectedDate] = useState('');
  const [downloadAvailable, setDownloadAvailable] = useState(false); // State for download availability
  const hasBoundsBeenFitted = useRef(false);

  const logger = React.useMemo(() => new Logger(getLoggerBaseURL()), []);

  useEffect(() => {
    const fetchTempLocationLogs = async () => {
      try {
        const url = `/driver/getLocation/${driverEmail}`;
        logger.log(`Fetch URL: ${url}`, 'INFO');

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ id: selectedDriverId }),
        });

        if (!response.ok) {
          throw new Error('Failed to fetch location logs');
        }

        const data = await response.json();

        if (data && Array.isArray(data.tempLocationLogs)) {
          const filteredLogs = [];

          data.tempLocationLogs.forEach((log) => {
            if (
              log.tempLocation !== 'No match or not updated yet' &&
              log.tempLocation !== 'Not logged because the five-minute condition was not met'
            ) {
              const [lat, lng] = log.tempLocation.split(',').map(Number);

              if (filteredLogs.length === 0) {
                filteredLogs.push(log); // Always include the first log
              } else {
                const lastLog = filteredLogs[filteredLogs.length - 1];
                const [lastLat, lastLng] = lastLog.tempLocation.split(',').map(Number);

                const distance = calculateDistance(lat, lng, lastLat, lastLng);
                if (distance > 50) {
                  filteredLogs.push(log); // Only include logs > 50 meters apart
                }
              }
            }
          });

          setTempLocationLogs(filteredLogs);
        } else {
          logger.log('No tempLocationLogs found in the response', 'WARN');
        }
      } catch (error) {
        logger.log(`Error fetching location logs: ${error.message}`, 'ERROR');
        setError(error.message);
      }
    };

    if (selectedDriverId) {
      logger.log(`Selected Driver ID: ${selectedDriverId}`, 'INFO');
      fetchTempLocationLogs();
    }
  }, [selectedDriverId, driverEmail, logger]);

  // Haversine formula to calculate the distance between two lat/lng points
  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const toRadians = (degree) => degree * (Math.PI / 180);
    const R = 6371e3; // Earth's radius in meters

    const φ1 = toRadians(lat1);
    const φ2 = toRadians(lat2);
    const Δφ = toRadians(lat2 - lat1);
    const Δλ = toRadians(lng2 - lng1);

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) *
      Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  };


  useEffect(() => {
    if (selectedDate) {
      const logsForDate = tempLocationLogs.filter(log => {
        const logDate = new Date(log.timestamp).toISOString().split('T')[0];
        return logDate === selectedDate;
      });

      setFilteredLogs(logsForDate);
      setGroupedLogs([]);
      hasBoundsBeenFitted.current = false;

      if (logsForDate.length === 0) {
        setError('No logs found for the selected date.');
        setDownloadAvailable(false); // Disable download if no logs found
      } else {
        setError(null);
        setDownloadAvailable(true); // Enable download if logs are found
      }
    } else {
      setFilteredLogs([]);
      setError(null);
      setDownloadAvailable(false);
    }
  }, [selectedDate, tempLocationLogs]);

  useEffect(() => {
    if (!selectedDate) {
      toast.info('Please select a date to view the logs.', { autoClose: 5000 });
    }
  }, [selectedDate]);

  useEffect(() => {
    const groupLogsByLocation = logs => {
      const grouped = {};

      logs.forEach(log => {
        const [lat, lng] = log.tempLocation.split(',').map(coord => parseFloat(coord));
        const key = `${lat.toFixed(6)},${lng.toFixed(6)}`;

        if (!grouped[key]) {
          grouped[key] = {
            tempLocation: log.tempLocation,
            totalTime: 0,
            lat,
            lng,
          };
        }

        grouped[key].totalTime += 5; // Assuming each log represents 5 minutes
      });

      setGroupedLogs(Object.values(grouped));
    };

    if (filteredLogs.length > 0) {
      groupLogsByLocation(filteredLogs);
    }
  }, [filteredLogs]);

  useEffect(() => {
    const loadGoogleMapsScript = () => {
      if (window.google && window.google.maps) {
        initializeMaps();
        return;
      }

      const existingScript = document.getElementById('google-maps-script');
      if (!existingScript) {
        const script = document.createElement('script');
        script.id = 'google-maps-script';
        script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyASvHxca4NGb5ObLmhETGUOmfWDMpcdmUg&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = initializeMaps;
        document.body.appendChild(script);
      } else {
        existingScript.onload = initializeMaps;
      }
    };

    const initializeMaps = () => {
      const mapsArray = [];
      const infoWindowsArray = [];
      const directionsServicesArray = [];
      const directionsRenderersArray = [];

      groupedLogs.reduce((acc, curr, index) => {
        if (index % MAX_WAYPOINTS === 0) {
          const map = new window.google.maps.Map(document.getElementById(`map-${acc.length}`), {
            center: { lat: 0, lng: 0 },
            zoom: 2,
          });
          mapsArray.push(map);
          infoWindowsArray.push(new window.google.maps.InfoWindow({ maxWidth: 20 }));
          directionsServicesArray.push(new window.google.maps.DirectionsService());
          directionsRenderersArray.push(new window.google.maps.DirectionsRenderer({ map }));
          acc.push([]);
        }
        acc[acc.length - 1].push(curr);
        return acc;
      }, []);

      setMaps(mapsArray);
      setInfoWindows(infoWindowsArray);
      setDirectionsServices(directionsServicesArray);
      setDirectionsRenderers(directionsRenderersArray);
    };

    loadGoogleMapsScript();
  }, [groupedLogs]);

  useEffect(() => {
    maps.forEach((map, mapIndex) => {
      if (map && groupedLogs.length > 0) {
        if (!hasBoundsBeenFitted.current) {
          const bounds = new window.google.maps.LatLngBounds();

          groupedLogs.slice(mapIndex * MAX_WAYPOINTS, (mapIndex + 1) * MAX_WAYPOINTS).forEach(log => {
            const position = { lat: log.lat, lng: log.lng };

            // Create the marker
            const marker = new window.google.maps.Marker({
              position,
              map,
              label: {
                text: `Time: ${formatTotalTime(log.totalTime)}`,
                color: 'black', // Customize the label color if necessary
                fontSize: '12px',
              }
            });

            // Automatically open the InfoWindow when the marker is added
            const infoWindowContent = `Location: (${log.lat.toFixed(6)}, ${log.lng.toFixed(6)})<br/>Total time: ${formatTotalTime(log.totalTime)}`;

            const infoWindow = new window.google.maps.InfoWindow({
              content: infoWindowContent,
            });

            infoWindow.open(map, marker); // Open the InfoWindow as soon as the marker is placed

            marker.addListener('click', () => {
              infoWindow.setContent(infoWindowContent); // If clicked again, show the info window with details
              infoWindow.open(map, marker);
            });

            bounds.extend(position); // Extend bounds to include this position
          });

          map.fitBounds(bounds); // Fit the map to the new bounds
          hasBoundsBeenFitted.current = true;
        }

        if (groupedLogs.length > 1) {
          const logsSlice = groupedLogs.slice(mapIndex * MAX_WAYPOINTS, (mapIndex + 1) * MAX_WAYPOINTS);
          const waypoints = logsSlice.slice(1, -1).map(log => ({
            location: { lat: log.lat, lng: log.lng },
            stopover: true,
          }));

          const origin = { lat: logsSlice[0].lat, lng: logsSlice[0].lng };
          const destination = { lat: logsSlice[logsSlice.length - 1].lat, lng: logsSlice[logsSlice.length - 1].lng };

          directionsServices[mapIndex].route(
            {
              origin,
              destination,
              waypoints,
              travelMode: window.google.maps.TravelMode.DRIVING,
            },
            (result, status) => {
              if (status === window.google.maps.DirectionsStatus.OK) {
                directionsRenderers[mapIndex].setDirections(result);
              } else {
                logger.log(`Error fetching directions: ${status}`, 'ERROR');
              }
            }
          );
        }
      }
    });
  }, [maps, groupedLogs, infoWindows, directionsServices, directionsRenderers, logger]);

  const formatTotalTime = minutes => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const handleDateChange = event => {
    setSelectedDate(event.target.value);
  };

  const handleDownloadMap = () => {
    if (!maps[0]) return;

    html2canvas(document.getElementById('map-0')).then(canvas => {
      canvas.toBlob(blob => {
        saveAs(blob, `driver-route-${selectedDriverId}-${selectedDate}.png`);
      });
    });
  };

  const handleDownloadLogs = () => {
    const logsCSV = filteredLogs.map(log => ({
      timestamp: log.timestamp,
      location: log.tempLocation,
    }));

    const csvContent = 'data:text/csv;charset=utf-8,' +
      ['timestamp,location', ...logsCSV.map(log => `${log.timestamp},${log.location}`)].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `logs-${selectedDriverId}-${selectedDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className='bg'>
      <Navbar />

      <h1 className='update-header'>Track Driver's Route</h1>

      <div className='form-groupUp'>
        <label>Select Date:</label>
        <input type='date' value={selectedDate} onChange={handleDateChange} className='UpDate' autocomplete="off" />
      </div>

      <div className='buttons-container'>
        {downloadAvailable && (
          <>
            <button className='map-button' onClick={handleDownloadMap}>Download Map</button>
            <button className='map-button' onClick={handleDownloadLogs}>Download Hault </button>
          </>
        )}
      </div>

      {error && <p className='error-message'>{error}</p>}

      {filteredLogs.length > 0 && (
        <div>
          <h4>Route for driver: {driverEmail}</h4>
          <div id='map-0'></div>
        </div>
      )}

      <ToastContainer />
    </div>

  );
};

export default Update;